import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  addAdditionalColleagues,
  getClassesBySchoolId,
  getTeacherAccountLookup,
  getTeachersBySchoolId,
  requestPasswordReset,
  shareClass
} from "../services/APIClient";
import "./TeacherOfferToAddTeacherModal.css";

const GRADE_LEVELS = [
  { value: "grade_level1", label: "Junior Infants" },
  { value: "grade_level2", label: "Senior Infants" },
  { value: "grade_level3", label: "1st Class" },
  { value: "grade_level4", label: "2nd Class" },
  { value: "grade_level5", label: "3rd Class" },
  { value: "grade_level6", label: "4th Class" },
  { value: "grade_level7", label: "5th Class" },
  { value: "grade_level8", label: "6th Class" }
];

const emptyTeacher = () => ({
  id: `${Date.now()}-${Math.random()}`,
  name: "",
  email: "",
  existingAction: "shareClasses"
});

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const firstPresentValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

const SETUP_LINK_STATUSES = new Set([
  "awaiting setup",
  "invitation sent",
  "invite sent",
  "invited",
  "not set up",
  "not setup",
  "pending",
  "pending teacher",
  "pending_teacher",
  "setup incomplete",
  "setup not completed",
  "setup_not_completed"
]);

const getClassId = (classItem) => classItem?.id || classItem?._id || classItem?.classid || classItem?.classId;

const getGradeLabelFromClass = (classItem) => {
  const grade = GRADE_LEVELS.find((level) => classItem?.[level.value]);
  return grade?.label || classItem?.grade || classItem?.gradeLevel || "";
};

const getAcademicYearFromClass = (classItem) =>
  classItem?.academicYear ||
  classItem?.academic_year ||
  classItem?.academicyear ||
  classItem?.schoolYear ||
  classItem?.yearLabel ||
  "";

const normaliseClass = (classItem) => {
  const grade = getGradeLabelFromClass(classItem);
  const academicYear = getAcademicYearFromClass(classItem);
  const studentCount =
    classItem?.studentNumber ??
    classItem?.studentCount ??
    classItem?.studentsCount ??
    classItem?.students?.length;
  const studentCopy =
    Number.isFinite(Number(studentCount))
      ? `${studentCount} ${Number(studentCount) === 1 ? "child" : "children"}`
      : "";
  const detailLabel = [grade, academicYear, studentCopy].filter(Boolean).join(", ");
  const name = classItem?.name || classItem?.className || classItem?.classname || "Unnamed Class";

  return {
    ...classItem,
    id: getClassId(classItem),
    name,
    grade,
    academicYear,
    detailLabel,
    searchText: [name, grade, academicYear].filter(Boolean).join(" ").toLowerCase()
  };
};

const splitList = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap(splitList);
  }

  if (value && typeof value === "object") {
    return [];
  }

  return String(value || "")
    .split(/[\n,]+/)
    .map(normaliseText)
    .filter(Boolean);
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const splitName = (name) => {
  const parts = normaliseText(name).split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ")
  };
};

const getTeacherEmail = (teacher) =>
  firstPresentValue(
    teacher?.email,
    teacher?.teacherEmail,
    teacher?.teacher_email,
    teacher?.emailAddress,
    teacher?.email_address,
    teacher?.pendingEmail,
    teacher?.pending_email,
    teacher?.inviteEmail,
    teacher?.invite_email,
    teacher?.user?.email,
    teacher?.teacher?.email,
    teacher?.pendingTeacher?.email
  );

const getPendingTeacherId = (teacher) =>
  firstPresentValue(
    teacher?.pendingteacherid,
    teacher?.pendingTeacherId,
    teacher?.pendingTeacherID,
    teacher?.pending_teacher_id,
    teacher?.pending_teacherid,
    teacher?.pendingTeacher?.id,
    teacher?.pending_teacher?.id
  );

const getTeacherStatus = (teacher) =>
  firstPresentValue(
    teacher?.status,
    teacher?.accountStatus,
    teacher?.account_status,
    teacher?.userStatus,
    teacher?.user_status,
    teacher?.inviteStatus,
    teacher?.invite_status
  );

const normaliseActions = (actions) => {
  if (Array.isArray(actions)) return actions;
  if (actions) return [actions];
  return [];
};

const teacherNeedsSetupLink = (teacher) => {
  const status = normaliseComparableText(teacher?.status).replace(/-/g, " ");
  const availableActions = normaliseActions(teacher?.availableActions || teacher?.available_actions);

  return (
    availableActions.includes("resend_setup_link") ||
    SETUP_LINK_STATUSES.has(status) ||
    Boolean(teacher?.pendingteacherid && !teacher?.canShareClasses)
  );
};

const findExactEmailMatch = (teachers, email) => {
  const targetEmail = normaliseText(email).toLowerCase();

  if (!targetEmail) {
    return null;
  }

  return teachers.find((teacher) => normaliseText(teacher.email).toLowerCase() === targetEmail) || null;
};

const normaliseTeacher = (teacher) => {
  const email = getTeacherEmail(teacher);
  const pendingteacherid = getPendingTeacherId(teacher);
  const status = getTeacherStatus(teacher);
  const activeTeacherId = firstPresentValue(
    teacher?.teacherid,
    teacher?.teacherId,
    teacher?.teacher_id,
    teacher?.teacher?.id
  );
  const id = firstPresentValue(
    pendingteacherid,
    teacher?.id,
    teacher?._id,
    activeTeacherId,
    teacher?.userId,
    teacher?.user_id,
    email
  );
  const isPendingTeacher = Boolean(pendingteacherid) || SETUP_LINK_STATUSES.has(normaliseComparableText(status).replace(/-/g, " "));
  const shareTeacherId = firstPresentValue(activeTeacherId, !isPendingTeacher ? teacher?.id || teacher?._id : "");

  return {
    ...teacher,
    id,
    pendingteacherid,
    shareTeacherId,
    canShareClasses: Boolean(shareTeacherId),
    name:
      teacher?.name ||
      teacher?.fullName ||
      teacher?.displayName ||
      [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
      "Unnamed teacher",
    email,
    status,
    availableActions: normaliseActions(teacher?.availableActions || teacher?.available_actions)
  };
};

const getTeacherRows = (teachersResponse) => {
  if (Array.isArray(teachersResponse)) return teachersResponse;
  if (Array.isArray(teachersResponse?.teachers)) return teachersResponse.teachers;
  if (Array.isArray(teachersResponse?.rows)) return teachersResponse.rows;
  if (Array.isArray(teachersResponse?.data)) return teachersResponse.data;
  return [];
};

const getAccountLookupRows = (lookupResponse) => {
  if (Array.isArray(lookupResponse)) return lookupResponse;
  if (Array.isArray(lookupResponse?.data)) return lookupResponse.data;

  const collectionKeys = [
    "teachers",
    "rows",
    "accounts",
    "activeTeachers",
    "active_teachers",
    "pendingTeachers",
    "pending_teachers",
    "invitedTeachers",
    "invited_teachers",
    "pending",
    "invites",
    "users"
  ];

  return collectionKeys.flatMap((key) => {
    const directRows = lookupResponse?.[key];
    const dataRows = lookupResponse?.data?.[key];

    if (Array.isArray(directRows)) return directRows;
    if (Array.isArray(dataRows)) return dataRows;
    return [];
  });
};

const mergeTeacherRows = (...teacherGroups) => {
  const teachersByKey = new Map();

  teacherGroups.flat().forEach((teacher) => {
    const normalisedTeacher = normaliseTeacher(teacher);
    const key = normaliseComparableText(normalisedTeacher.email) || normalisedTeacher.id;

    if (!key) {
      return;
    }

    const existingTeacher = teachersByKey.get(key) || {};

    teachersByKey.set(key, {
      ...existingTeacher,
      ...normalisedTeacher,
      pendingteacherid: normalisedTeacher.pendingteacherid || existingTeacher.pendingteacherid,
      shareTeacherId: existingTeacher.shareTeacherId || normalisedTeacher.shareTeacherId,
      canShareClasses: Boolean(existingTeacher.canShareClasses || normalisedTeacher.canShareClasses),
      availableActions: [
        ...new Set([
          ...(existingTeacher.availableActions || []),
          ...(normalisedTeacher.availableActions || [])
        ])
      ]
    });
  });

  return Array.from(teachersByKey.values());
};

const getPrefillTeachers = (prefill) => {
  const teacherItems = Array.isArray(prefill?.teachers) ? prefill.teachers : [];
  const teacherRows = teacherItems
    .map((teacher) => ({
      id: `${Date.now()}-${Math.random()}`,
      name: normaliseText(teacher?.teacherName || teacher?.name),
      email: normaliseText(teacher?.email || teacher?.teacherEmail),
      existingAction: "shareClasses"
    }))
    .filter((teacher) => teacher.name || teacher.email);

  const teacherNames = splitList(prefill?.teacherNames || prefill?.teacher_names);
  const teacherEmails = splitList(
    prefill?.teacherEmails ||
      prefill?.teacher_emails
  );
  const fallbackTeacherEmail = normaliseText(prefill?.email || prefill?.teacherEmail);

  teacherNames.forEach((teacherName, index) => {
    teacherRows.push({
      id: `${Date.now()}-${Math.random()}`,
      name: teacherName,
      email: normaliseText(teacherEmails[index]) || (index === 0 ? fallbackTeacherEmail : ""),
      existingAction: "shareClasses"
    });
  });

  if (teacherRows.length < teacherEmails.length) {
    teacherEmails.slice(teacherRows.length).forEach((teacherEmail) => {
      teacherRows.push({
        id: `${Date.now()}-${Math.random()}`,
        name: "",
        email: normaliseText(teacherEmail),
        existingAction: "shareClasses"
      });
    });
  }

  if (teacherRows.length < 1 && (prefill?.teacherName || prefill?.email)) {
    teacherRows.push({
      id: `${Date.now()}-${Math.random()}`,
      name: normaliseText(prefill?.teacherName || prefill?.teacher_name),
      email: normaliseText(prefill?.email || prefill?.teacherEmail),
      existingAction: "shareClasses"
    });
  }

  return teacherRows.length > 0 ? teacherRows : [emptyTeacher()];
};

const normalisePrefill = (prefill) => ({
  teachers: getPrefillTeachers(prefill || {}),
  classNames: splitList(
    prefill?.classNames ||
      prefill?.class_names ||
      prefill?.className ||
      prefill?.class_name
  )
});

const getPrefilledClasses = (classes, classNames) => {
  const selectedById = new Map();

  classNames.some((className) => {
    const matches = classes.filter(
      (classItem) => normaliseComparableText(classItem.name) === normaliseComparableText(className)
    );

    if (matches.length === 1) {
      selectedById.set(matches[0].id, matches[0]);
      return true;
    }

    return false;
  });

  return Array.from(selectedById.values());
};

const buildTeacherInviteRows = (teachers, selectedClasses, needsClassAccess) => {
  const classIds = needsClassAccess ? selectedClasses.map((classItem) => classItem.id).filter(Boolean) : [];

  if (classIds.length < 1) {
    return teachers;
  }

  return teachers.map((teacher) => ({
    ...teacher,
    sharedClassId: classIds[0]
  }));
};

function TeacherOfferToAddTeacherModal({
  show,
  handleClose,
  schoolid,
  rollnumber = "",
  rollNumber = "",
  currentTeacherName = "",
  countryId = null,
  prefill = null,
  onInviteTeacher = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const [stage, setStage] = useState("form");
  const [teachers, setTeachers] = useState([emptyTeacher()]);
  const [schoolTeachers, setSchoolTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [classAccess, setClassAccess] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [completedSummary, setCompletedSummary] = useState(null);
  const [inviteResults, setInviteResults] = useState([]);

  const selectedClassNames = selectedClasses.map((classItem) => classItem.name);
  const teachersWithExistingMatch = teachers.map((teacher) => ({
    ...teacher,
    existingTeacher: findExactEmailMatch(schoolTeachers, teacher.email)
  }));
  const matchedTeachers = teachersWithExistingMatch.filter((teacher) => teacher.existingTeacher);
  const existingTeachers = matchedTeachers.filter((teacher) => !teacherNeedsSetupLink(teacher.existingTeacher));
  const alreadyInvitedTeachers = matchedTeachers.filter((teacher) => teacherNeedsSetupLink(teacher.existingTeacher));
  const newTeachers = teachers.filter(
    (teacher) => !findExactEmailMatch(schoolTeachers, teacher.email)
  );
  const hasInvalidTeacher = teachers.some(
    (teacher) => !normaliseText(teacher.name) || !isValidEmail(normaliseText(teacher.email))
  );
  const needsClassAccess = classAccess === "add";
  const classAccessChosen = classAccess === "add" || classAccess === "none";
  const canSend =
    teachers.length > 0 &&
    !hasInvalidTeacher &&
    classAccessChosen &&
    (!needsClassAccess || selectedClasses.length > 0);

  const filteredClasses = useMemo(() => {
    const normalisedSearchTerm = normaliseComparableText(classSearchTerm);

    if (!normalisedSearchTerm) {
      return classes;
    }

    return classes.filter((classItem) => classItem.searchText.includes(normalisedSearchTerm));
  }, [classes, classSearchTerm]);

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("form");
    setTeachers(prefillContext.teachers);
    setSchoolTeachers([]);
    setClasses([]);
    setClassSearchTerm(prefillContext.classNames.join(", "));
    setSelectedClasses([]);
    setClassAccess(prefillContext.classNames.length > 0 ? "add" : "");
    setSubmitAttempted(false);
    setIsSubmitting(false);
    setError("");
    setCompletedSummary(null);
    setInviteResults([]);

    if (!schoolid) {
      setError("School details could not be loaded. Please try again.");
      return;
    }

    setIsLoadingContext(true);
    Promise.allSettled([
      getTeachersBySchoolId(schoolid),
      getClassesBySchoolId(schoolid),
      getTeacherAccountLookup(schoolid)
    ])
      .then(([teachersResult, classesResult, accountLookupResult]) => {
        const teachersResponse = teachersResult.status === "fulfilled" ? teachersResult.value : [];
        const classesResponse = classesResult.status === "fulfilled" ? classesResult.value : [];
        const accountLookupResponse = accountLookupResult.status === "fulfilled" ? accountLookupResult.value : [];
        const normalisedTeachers = mergeTeacherRows(
          getTeacherRows(teachersResponse),
          getAccountLookupRows(accountLookupResponse)
        )
          .filter((teacher) => teacher.id && teacher.email);
        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map(normaliseClass)
          .filter((classItem) => classItem.id)
          .filter((classItem) => classItem.active !== false)
          .filter((classItem) => !classItem.snapshot && !classItem.trial_class)
          .sort((firstClass, secondClass) => {
            if (firstClass.academicYear !== secondClass.academicYear) {
              return secondClass.academicYear?.localeCompare(firstClass.academicYear || "") || 0;
            }

            return firstClass.name.localeCompare(secondClass.name);
          });
        setSchoolTeachers(normalisedTeachers);
        setClasses(normalisedClasses);
        setSelectedClasses(getPrefilledClasses(normalisedClasses, prefillContext.classNames));

        if (teachersResult.status === "rejected" && accountLookupResult.status === "rejected") {
          setError("Teacher details could not be loaded. Please try again.");
        } else if (classesResult.status === "rejected") {
          setError("Class details could not be loaded. Please try again.");
        }
      })
      .catch((loadError) => {
        console.error("Error loading offer-to-add-teacher context:", loadError);
        setError("Teacher and school details could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingContext(false);
      });
  }, [show, schoolid, prefillContext]);

  const updateTeacher = (id, field, value) => {
    setTeachers((currentTeachers) =>
      currentTeachers.map((teacher) =>
        teacher.id === id ? { ...teacher, [field]: value } : teacher
      )
    );
  };

  const addTeacherRow = () => {
    setTeachers((currentTeachers) => [...currentTeachers, emptyTeacher()]);
    setSubmitAttempted(false);
    setError("");
  };

  const removeTeacherRow = (id) => {
    setTeachers((currentTeachers) =>
      currentTeachers.length > 1
        ? currentTeachers.filter((teacher) => teacher.id !== id)
        : currentTeachers
    );
    setSubmitAttempted(false);
    setError("");
  };

  const toggleClass = (classItem) => {
    setSelectedClasses((currentClasses) => {
      if (currentClasses.some((selectedClass) => selectedClass.id === classItem.id)) {
        return [];
      }

      return [classItem];
    });
    setError("");
  };

  const clearClassAccess = () => {
    setClassAccess("none");
    setSelectedClasses([]);
    setClassSearchTerm("");
    setError("");
  };

  const sendNewTeacherInvites = async () => {
    if (newTeachers.length < 1) {
      return;
    }

    const colleagues = newTeachers.map((teacher) => {
      const nameParts = splitName(teacher.name);

      return {
        firstName: nameParts.firstName,
        lastName: nameParts.lastName,
        email: normaliseText(teacher.email),
        roles: "Class teacher"
      };
    });

    const inviteRows = buildTeacherInviteRows(colleagues, selectedClasses, needsClassAccess);
    const responses = await Promise.all(
      inviteRows.map((teacher) =>
        addAdditionalColleagues(
          schoolid,
          rollnumber || rollNumber,
          [teacher],
          currentTeacherName,
          countryId
        )
      )
    );
    const rows = responses.flatMap((response, index) => {
      const responseRows = response?.results || response?.rows || response?.users || response || [];

      if (Array.isArray(responseRows)) {
        return responseRows;
      }

      return [{
        email: inviteRows[index].email,
        status: "invited"
      }];
    });

    setInviteResults(rows.length > 0 ? rows : inviteRows.map((teacher) => ({
      email: teacher.email,
      status: "invited"
    })));
  };

  const sendInvites = async () => {
    setSubmitAttempted(true);

    if (!canSend) {
      return;
    }

    if (existingTeachers.length > 0) {
      setTeachers((currentTeachers) =>
        currentTeachers.map((teacher) =>
          findExactEmailMatch(schoolTeachers, teacher.email)
            ? { ...teacher, existingAction: needsClassAccess ? "shareClasses" : "passwordReset" }
            : teacher
        )
      );
      setStage("existing");
      return;
    }

    if (!schoolid) {
      setError("Teacher invites cannot be sent until school context is available.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await sendNewTeacherInvites();
      setCompletedSummary({
        invitedCount: newTeachers.length,
        existingCount: 0,
        alreadyInvitedCount: alreadyInvitedTeachers.length,
        classNames: needsClassAccess ? selectedClassNames : []
      });
      setStage("success");
    } catch (inviteError) {
      console.error("Error sending teacher invites:", inviteError);
      setError("The teacher invites could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const completeExistingActions = async () => {
    const existingAction = existingTeachers[0]?.existingAction;

    if (existingAction === "shareClasses") {
      if (selectedClasses.length < 1) {
        setError("Choose a class before sharing class access.");
        return;
      }
    }

    setIsSubmitting(true);
    setError("");

    try {
      if (existingAction === "passwordReset") {
        await Promise.all(
          existingTeachers.map((teacher) =>
            requestPasswordReset(teacher.existingTeacher.email)
          )
        );
      }

      if (existingAction === "shareClasses") {
        await Promise.all(
          existingTeachers.flatMap((teacher) =>
            selectedClasses.map((classItem) =>
              shareClass(classItem.id, teacher.existingTeacher.shareTeacherId || teacher.existingTeacher.id)
            )
          )
        );
      }

      await sendNewTeacherInvites();

      setCompletedSummary({
        invitedCount: newTeachers.length,
        existingCount: existingTeachers.length,
        alreadyInvitedCount: alreadyInvitedTeachers.length,
        classNames: existingAction === "shareClasses" ? selectedClassNames : []
      });
      setStage("success");
    } catch (actionError) {
      console.error("Error handling existing teacher:", actionError);
      setError("The existing teacher action could not be completed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForMoreTeachers = () => {
    setStage("form");
    setTeachers([emptyTeacher()]);
    setClassAccess("");
    setClassSearchTerm("");
    setSelectedClasses([]);
    setSubmitAttempted(false);
    setError("");
    setCompletedSummary(null);
    setInviteResults([]);
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: inviteResults[0]?.email || normaliseText(teachers[0]?.email) || "",
        message: "Add teacher workflow complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderTeacherRows = () =>
    teachers.map((teacher, index) => {
      const nameInvalid = submitAttempted && !normaliseText(teacher.name);
      const emailInvalid = submitAttempted && !isValidEmail(normaliseText(teacher.email));

      return (
        <div className="offer-add-teacher-row" key={teacher.id}>
          <div className="offer-add-teacher-row-header">
            <h3 className="offer-add-teacher-row-title">Teacher {index + 1}</h3>
            {teachers.length > 1 && (
              <Button
                variant="link"
                type="button"
                onClick={() => removeTeacherRow(teacher.id)}
                aria-label={`Remove teacher ${index + 1}`}
              >
                Remove
              </Button>
            )}
          </div>
          <Form.Group className="offer-add-teacher-field" controlId={`offerAddTeacherName-${teacher.id}`}>
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={teacher.name}
              onChange={(event) => updateTeacher(teacher.id, "name", event.target.value)}
              isInvalid={nameInvalid}
              placeholder="Teacher name"
              aria-label="Teacher name"
            />
            <Form.Control.Feedback type="invalid">Enter the teacher's name.</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="offer-add-teacher-field" controlId={`offerAddTeacherEmail-${teacher.id}`}>
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={teacher.email}
              onChange={(event) => updateTeacher(teacher.id, "email", event.target.value)}
              isInvalid={emailInvalid}
              placeholder="teacher@example.com"
            />
            <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
          </Form.Group>
        </div>
      );
    });

  const renderClassResults = () => {
    if (isLoadingContext) {
      return (
        <div className="offer-add-teacher-loading">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (filteredClasses.length < 1) {
      return <p className="offer-add-teacher-muted">No matching classes found.</p>;
    }

    return filteredClasses.map((classItem) => {
      const selected = selectedClasses.some((selectedClass) => selectedClass.id === classItem.id);

      return (
        <button
          key={classItem.id}
          type="button"
          className={`offer-add-teacher-class-result${selected ? " selected" : ""}`}
          onClick={() => toggleClass(classItem)}
          aria-label={`${selected ? "Remove" : "Select"} ${classItem.name}`}
          aria-pressed={selected}
        >
          <span className="offer-add-teacher-check" aria-hidden="true" />
          <span className="offer-add-teacher-class-copy">
            <strong>{classItem.name}</strong>
            {classItem.detailLabel && <span>{classItem.detailLabel}</span>}
          </span>
          {selected && <span className="offer-add-teacher-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderSelectedClassSummary = () => {
    if (selectedClasses.length < 1) {
      return "No class selected.";
    }

    return selectedClassNames[0];
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Offer To Add Teacher</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="offer-add-teacher-intro">Use this when you need to invite more than one teacher to ALPACA.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        {isLoadingContext && (
          <div className="offer-add-teacher-loading">
            <Spinner animation="border" size="sm" />
            <span>Loading school teacher details...</span>
          </div>
        )}

        <div className="offer-add-teacher-rows">{renderTeacherRows()}</div>
        <Button
          variant="outline-primary"
          type="button"
          className="offer-add-teacher-add-row"
          onClick={addTeacherRow}
        >
          Add teacher
        </Button>

        <Form.Group className="offer-add-teacher-section" controlId="offerAddTeacherClassAccess">
          <Form.Label>Do they need class access now?</Form.Label>
          <div className="offer-add-teacher-options">
            <label className={`offer-add-teacher-option${classAccess === "none" ? " selected" : ""}`}>
              <Form.Check
                type="radio"
                name="offerAddTeacherClassAccess"
                checked={classAccess === "none"}
                onChange={clearClassAccess}
              />
              <span>
                <strong>No class yet</strong>
                <span>They can be assigned later</span>
              </span>
            </label>
            <label className={`offer-add-teacher-option${classAccess === "add" ? " selected" : ""}`}>
              <Form.Check
                type="radio"
                name="offerAddTeacherClassAccess"
                checked={classAccess === "add"}
                onChange={() => {
                  setClassAccess("add");
                  setError("");
                }}
              />
              <span>
                <strong>Add class access</strong>
                <span>Search and select one class after inviting</span>
              </span>
            </label>
          </div>
        </Form.Group>

        {needsClassAccess && (
          <Form.Group className="offer-add-teacher-section" controlId="offerAddTeacherClassNames">
            <Form.Label>Class name</Form.Label>
            <Form.Control
              type="search"
              value={classSearchTerm}
              onChange={(event) => {
                setClassSearchTerm(event.target.value);
                setError("");
              }}
              isInvalid={submitAttempted && selectedClasses.length < 1}
              placeholder="Start typing a class name"
              aria-label="Search class"
            />
            <div className="offer-add-teacher-class-results" aria-label="Matching classes">
              {renderClassResults()}
            </div>
            <Form.Text className="text-muted">
              {renderSelectedClassSummary()}
            </Form.Text>
            <Form.Control.Feedback type="invalid">
              Choose one class or choose No class yet.
            </Form.Control.Feedback>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="offer-add-teacher-footer-note">
          {canSend
            ? teachers.length === 1 ? "One teacher ready to invite." : `${teachers.length} teachers ready to invite.`
            : "Enter teacher details and choose class access."}
        </span>
        <Button variant="outline-secondary" type="button" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" type="button" onClick={sendInvites} disabled={!canSend || isSubmitting || isLoadingContext}>
          {isSubmitting ? "Sending..." : "Send invites"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderExisting = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Teacher Already Exists</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="offer-add-teacher-intro">
          {existingTeachers.length === 1
            ? "One email already has an ALPACA account."
            : `${existingTeachers.length} emails already have ALPACA accounts.`}
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning" className="offer-add-teacher-warning">
          <strong>
            Choose what to do with {existingTeachers.length === 1 ? existingTeachers[0]?.name || "this teacher" : "these teachers"}
          </strong>
          <div>Avoid sending duplicate invites. Help the existing account instead.</div>
        </Alert>
        <h3 className="offer-add-teacher-question">Next action</h3>
        <div className="offer-add-teacher-options">
          {[
            ["passwordReset", "Send password reset", existingTeachers.length === 1 ? `Help ${existingTeachers[0]?.name || "this teacher"} access her existing account` : "Help these teachers access their existing accounts"],
            ["shareClasses", "Share classes", selectedClasses.length > 0 ? renderSelectedClassSummary() : "Go back and choose classes first."],
            ["skip", "Skip this teacher", existingTeachers.length === 1 ? `Do not send anything for ${existingTeachers[0]?.name || "this teacher"}` : "Do not send anything for these teachers"]
          ].map(([value, label, helper]) => (
            <label
              className={`offer-add-teacher-option${existingTeachers[0]?.existingAction === value ? " selected" : ""}${value === "shareClasses" && selectedClasses.length < 1 ? " disabled" : ""}`}
              key={value}
            >
              <Form.Check
                type="radio"
                name="offerAddTeacherExistingAction"
                checked={existingTeachers[0]?.existingAction === value}
                disabled={value === "shareClasses" && selectedClasses.length < 1}
                onChange={() => updateTeacher(existingTeachers[0].id, "existingAction", value)}
              />
              <span>
                <strong>{label}</strong>
                <span>{helper}</span>
              </span>
            </label>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="offer-add-teacher-footer-note">Existing account route selected.</span>
        <Button variant="outline-secondary" type="button" onClick={() => setStage("form")} disabled={isSubmitting}>
          Back
        </Button>
        <Button variant="primary" type="button" onClick={completeExistingActions} disabled={isSubmitting}>
          {isSubmitting
            ? "Sending..."
            : existingTeachers[0]?.existingAction === "passwordReset"
            ? "Send password reset"
            : existingTeachers[0]?.existingAction === "skip"
              ? "Skip teacher"
              : "Share classes"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => {
    const hasNewInvites = completedSummary?.invitedCount > 0;
    const hasExistingActions = completedSummary?.existingCount > 0;
    const hasAlreadyInvited = completedSummary?.alreadyInvitedCount > 0;

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="h2">Teacher Updates Complete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success" className="offer-add-teacher-success">
            <span className="offer-add-teacher-ok-badge">OK</span>
            <span>
              <strong>
                {hasNewInvites
                  ? "The teacher invites have been sent."
                  : hasExistingActions
                    ? "The existing teacher action is complete."
                    : "The teacher invite is already pending."}
              </strong>
              <span>
                {hasNewInvites
                  ? "New teachers will receive an email to set up their account."
                  : hasAlreadyInvited
                    ? "No new invite email was sent."
                    : "No new teacher invite was sent for the existing account."}
              </span>
            </span>
          </Alert>
          <div className="offer-add-teacher-next">
            <strong>What next?</strong>
            <span>
              {embedded
                ? "Done returns to SENSAI. Add more teachers repeats the action."
                : "Done returns to Help Centre home. Add more teachers repeats the action."}
            </span>
          </div>
          {hasAlreadyInvited && (
            <div className="offer-add-teacher-next" aria-label="Already invited teachers">
              <strong>Already invited</strong>
              <span>
                {completedSummary.alreadyInvitedCount === 1
                  ? "One teacher already had an invite. No duplicate invite email was sent."
                  : `${completedSummary.alreadyInvitedCount} teachers already had invites. No duplicate invite emails were sent.`}
              </span>
            </div>
          )}
          {inviteResults.length > 0 && (
            <div className="offer-add-teacher-next" aria-label="Invite results">
              <strong>Invite results</strong>
              {inviteResults.map((result, index) => (
                <span key={`${result.email || result.name || "invite"}-${index}`}>
                  {result.email || result.name || `Teacher ${index + 1}`}: {result.status || "invited"}
                </span>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <span className="offer-add-teacher-footer-note">Task complete.</span>
          <Button variant="outline-secondary" type="button" onClick={resetForMoreTeachers}>
            Add more teachers
          </Button>
          {onInviteTeacher && (
            <Button
              variant="outline-secondary"
              type="button"
              onClick={() => {
                const firstTeacher = teachers[0] || {};
                onInviteTeacher({
                  teacherName: normaliseText(firstTeacher.name),
                  email: normaliseText(firstTeacher.email),
                  classNames: selectedClassNames
                });
              }}
            >
              Invite teacher to platform
            </Button>
          )}
          <Button variant="primary" type="button" onClick={handleDone}>
            Done
          </Button>
        </Modal.Footer>
      </>
    );
  };

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-offer-add-teacher-modal">
      {stage === "form" && renderForm()}
      {stage === "existing" && renderExisting()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherOfferToAddTeacherModal;
